import {
  Box,
  Center,
  Code,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertTriangle, Check, Copy, Home, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { iconSize } from "~/components/ui/icon-size";
import { useRouter } from "~/utils/compat/next-router";
import { captureException, toError } from "~/utils/posthogErrorCapture";
export function PageErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: (...args: unknown[]) => void;
}) {
  const router = useRouter();
  const [copied, setCopied] = useState(false);
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  const isDev = process.env.NODE_ENV === "development";

  useEffect(() => {
    captureException(toError(error), {
      tags: { source: "error-boundary" },
      extra: { pathname: window.location.pathname },
    });
  }, [error]);

  return (
    <Center minHeight="60vh" padding={8}>
      <VStack gap={6} maxWidth="560px" width="full">
        <VStack gap={3}>
          <Box padding={3} borderRadius="full" bg="err.soft">
            <AlertTriangle style={iconSize.lg} color="var(--color-err)" />
          </Box>
          <Heading size="md" color="fg">
            This page could not be shown
          </Heading>
          <Text textStyle="sm" color="fg.2" textAlign="center" maxWidth="400px">
            Sorry about that — our team has been notified and is looking into
            it. You can try again, or head back to the home page.
          </Text>
        </VStack>

        {isDev ? (
          <Box
            width="full"
            borderRadius="lg"
            border="1px solid"
            borderColor="border"
            overflow="hidden"
          >
            <HStack
              paddingX={4}
              paddingY={2}
              bg="surface.2"
              borderBottom="1px solid"
              borderColor="border"
              justify="space-between"
            >
              <Text textStyle="xs" fontWeight="medium" color="fg.2">
                Error details
              </Text>
              <Button
                size="2xs"
                variant="ghost"
                color="fg.2"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(stack ?? message);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch {
                    // Clipboard API unavailable or denied
                  }
                }}
              >
                {copied ? (
                  <Check style={iconSize.sm} />
                ) : (
                  <Copy style={iconSize.sm} />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
            </HStack>
            <Code
              display="block"
              paddingX={4}
              paddingY={3}
              maxHeight="180px"
              overflow="auto"
              textStyle="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              bg="surface"
              color="err"
              borderRadius={0}
            >
              {stack ?? message}
            </Code>
          </Box>
        ) : (
          <Text textStyle="sm" color="fg.2" textAlign="center">
            Error reference has been logged automatically.
          </Text>
        )}

        <HStack gap={3}>
          <Button size="sm" variant="outline" onClick={resetErrorBoundary}>
            <RotateCcw style={iconSize.sm} />
            Try again
          </Button>
          <Button
            size="sm"
            variant="ghost"
            color="fg.2"
            onClick={() => void router.push("/")}
          >
            <Home style={iconSize.sm} />
            Home
          </Button>
        </HStack>
      </VStack>
    </Center>
  );
}
